
import React, { useRef, useState, useEffect } from "react";
import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import { useProgress } from "@react-three/drei";

const PageReveal = ({ onComplete }) => {
    const containerRef = useRef(null);
    const moonRef = useRef(null);
    const wordsRef = useRef([]);
    const [progress, setProgress] = useState(0);
    const { active, progress: loadProgress /*, errors, item, loaded, total */ } = useProgress();
    const [isFullyLoaded, setIsFullyLoaded] = useState(false);

    // Failsafe: Force reveal after 6 seconds max
    useEffect(() => {
        const failsafe = setTimeout(() => {
            console.log("Loading failsafe triggered");
            setIsFullyLoaded(true);
            setMinTimePassed(true);
        }, 6000);
        return () => clearTimeout(failsafe);
    }, []);

    const words = ["IDEATING", "CRAFTING", "INNOVATING", "XPIN MEDIA"];

    // Update internal progress state based on THREE loading
    useEffect(() => {
        // If nothing is loading (no 3D content on this page), reveal immediately
        if (loadProgress === 0 && !active) {
            setProgress(100);
            setIsFullyLoaded(true); // instant — no 3D assets to load
            return;
        }

        if (loadProgress > progress) {
            setProgress(loadProgress);
        }

        if (loadProgress >= 100) {
            setIsFullyLoaded(true);
        }
    }, [loadProgress, active]);

    // Minimum display time logic for the animation to look good
    const [minTimePassed, setMinTimePassed] = useState(false);
    useEffect(() => {
        const timer = setTimeout(() => {
            setMinTimePassed(true);
        }, 4500); // Minimum 4.5 seconds of animation
        return () => clearTimeout(timer);
    }, []);

    const tl = useRef();

    useGSAP(() => {
        tl.current = gsap.timeline();

        // 1. Setup
        // Moon starts pushed down (behind the wall)
        gsap.set(moonRef.current, { y: 280, scale: 0.9 });
        gsap.set(".moon-haze", { opacity: 0 });
        gsap.set(wordsRef.current, { y: 20, opacity: 0 });

        // 2. Scene Animation
        // Moon Rises (Majestic & Slow)
        tl.current.to(moonRef.current, {
            y: 0,
            scale: 1, // Slight growth as it rises
            duration: 5.5,
            ease: "sine.out" // Very natural, non-mechanical ease
        }, 0);

        // Glow Fades In
        tl.current.to(".moon-haze", {
            opacity: 1,
            duration: 4,
            ease: "power1.inOut"
        }, 0.5);

        // 3. Text Sequence (Buttery Smooth)
        const wordTime = 5.0 / words.length;

        wordsRef.current.forEach((wordEl, i) => {
            if (!wordEl) return;
            const startTime = i * wordTime;
            const exitTime = startTime + wordTime;

            // Enter - Long fade, minimal movement for smoothness
            tl.current.fromTo(wordEl,
                { y: 15, opacity: 0 },
                { y: 0, opacity: 1, duration: 1.2, ease: "power2.out" },
                startTime
            );

            // Exit - Drift up and fade
            if (i < words.length - 1) {
                tl.current.to(wordEl, {
                    y: -15,
                    opacity: 0,
                    duration: 1.2,
                    ease: "power2.in"
                }, exitTime - 0.8); // Huge overlap for seamless transition
            }
        });

        // 4. Reveal Step
        tl.current.addLabel("reveal", 5.2);

        // PAUSE HERE until assets are ready
        tl.current.addPause("reveal");

        tl.current.call(() => {
            // Flash
            gsap.to(".flash", { opacity: 1, duration: 0.2, yoyo: true, repeat: 1 });
        }, null, "reveal");

        tl.current.call(onComplete, null, "reveal");

        // Fade out Scene Layer
        tl.current.to(".scene-layer", {
            opacity: 0,
            duration: 0.8
        }, "reveal");

        // 5. Shutter Strips Reveal
        tl.current.to(".strip", {
            height: "0%",
            duration: 1.4,
            stagger: 0.06,
            ease: "expo.inOut"
        }, "reveal+=0.1");

        tl.current.to(containerRef.current, { display: "none" });

    }, { scope: containerRef });

    // Resume timeline when loaded
    useEffect(() => {
        const checkAndPlay = () => {
            if (isFullyLoaded && minTimePassed && tl.current) {
                // Keep calling play if we are ready
                tl.current.play();
            }
        };

        checkAndPlay();
        const interval = setInterval(checkAndPlay, 500); // Polling failsafe
        return () => clearInterval(interval);
    }, [isFullyLoaded, minTimePassed]);

    return (
        <div
            ref={containerRef}
            className="fixed inset-0 z-[99999] flex flex-col items-center justify-center pointer-events-auto bg-transparent overflow-hidden"
        >
            {/* Background Strips (The actual Reveal Mechanism) - Base Layer */}
            <div className="absolute inset-0 flex z-10 pointer-events-none w-full h-full">
                {[...Array(5)].map((_, i) => (
                    <div
                        key={i}
                        className="strip flex-1 h-full bg-[#050505] border-r border-white/5 last:border-none relative"
                    ></div>
                ))}
            </div>

            {/* Flash Overlay */}
            <div className="flash fixed inset-0 bg-[#eef2ff] z-[60] opacity-0 pointer-events-none mix-blend-overlay"></div>

            {/* SCENE LAYER (The Visuals: Wall + Sky) - Z-20 */}
            <div className="scene-layer absolute inset-0 z-20 flex flex-col w-full h-full pointer-events-none">

                {/* SKY (Top Half) */}
                <div className="relative w-full h-[50vh] overflow-hidden bg-gradient-to-t from-[#0a0a0c] to-[#050505]">

                    {/* Atmospheric Haze (Glow) */}
                    <div className="moon-haze absolute bottom-0 left-1/2 -translate-x-1/2 w-[120vw] h-[80vh] bg-[radial-gradient(circle_at_center_bottom,rgba(164,186,220,0.15)_0%,rgba(0,0,0,0)_60%)] pointer-events-none mix-blend-screen translate-y-1/3 rounded-[100%] blur-3xl"></div>

                    {/* Moon Container */}
                    <div className="absolute bottom-[-100px] left-1/2 -translate-x-1/2 pb-12">
                        <div
                            ref={moonRef}
                            className="w-48 h-48 rounded-full relative z-30"
                            style={{
                                background: "#EAEBED",
                                boxShadow: `
inset - 20px - 20px 60px rgba(0, 0, 0, 0.4),  /* Shadow Side */
    inset 10px 10px 40px rgba(255, 255, 255, 0.8), /* Highlight Side */
        0 0 35px 5px rgba(200, 220, 255, 0.3), /* Outer Glow */
            0 0 90px 20px rgba(180, 200, 255, 0.1) /* Distant Glow */
                `
                            }}
                        >
                            {/* Realistic Craters (Soft & Irregular) */}
                            <div className="absolute top-[25%] right-[25%] w-10 h-8 rounded-[40%] bg-[#b0b3ba] opacity-30 blur-[4px]"></div>
                            <div className="absolute top-[50%] left-[20%] w-14 h-12 rounded-[45%] bg-[#a3a6ae] opacity-25 blur-[6px]"></div>
                            <div className="absolute bottom-[20%] right-[35%] w-8 h-8 rounded-full bg-[#9da0a8] opacity-20 blur-[3px]"></div>
                            <div className="absolute top-[15%] left-[45%] w-4 h-4 rounded-full bg-[#b0b3ba] opacity-30 blur-[2px]"></div>
                        </div>
                    </div>
                </div>

                {/* WALL (Bottom Half) */}
                <div className="relative w-full h-[50vh] bg-[#050505] border-t border-slate-700/30 z-40 shadow-[0_-20px_80px_rgba(0,0,0,0.9)] flex flex-col items-center justify-start pt-24">

                    {/* Text Container */}
                    <div className="relative h-12 w-full flex items-center justify-center overflow-visible">
                        {words.map((word, i) => (
                            <div
                                key={i}
                                ref={el => wordsRef.current[i] = el}
                                className="absolute text-sm md:text-xl font-[main] tracking-[0.5em] text-slate-300/90 font-light uppercase whitespace-nowrap drop-shadow-md"
                            >
                                {word}
                            </div>
                        ))}
                    </div>

                    {/* Counter */}
                    <div className="mt-12 font-[main] text-slate-500/40 text-[10px] tracking-widest font-mono">
                        {Math.round(progress)}%
                    </div>

                </div>

            </div>
        </div>
    );
};

export default PageReveal;

